import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CurrencyContext = createContext();

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

export const CurrencyProvider = ({ children }) => {
  const [displayCurrency, setDisplayCurrency] = useState(() => {
    return localStorage.getItem('displayCurrency') || 'original';
  });
  
  const [exchangeRates, setExchangeRates] = useState({
    usd_to_lrd: 183.0,
    lrd_to_usd: 0.00546,
    last_updated: null,
    source: 'default'
  });
  
  const [loading, setLoading] = useState(true);

  // Fetch exchange rates on mount
  const fetchRates = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/exchange-rates`);
      setExchangeRates(response.data);
    } catch (error) {
      console.error('Failed to fetch exchange rates:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRates();
    // Refresh rates every 30 minutes
    const interval = setInterval(fetchRates, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchRates]);

  // Save display preference
  useEffect(() => {
    localStorage.setItem('displayCurrency', displayCurrency);
  }, [displayCurrency]);

  // Convert amount between currencies
  const convertAmount = useCallback((amount, fromCurrency) => {
    if (!amount || isNaN(amount)) return 0;
    
    const numAmount = parseFloat(amount);
    
    if (fromCurrency === 'USD') {
      return numAmount * exchangeRates.usd_to_lrd;
    } else if (fromCurrency === 'LRD') {
      return numAmount * exchangeRates.lrd_to_usd;
    }
    return numAmount;
  }, [exchangeRates]);

  // Format currency amount with symbol
  const formatCurrency = useCallback((amount, currency = 'LRD') => {
    if (amount === null || amount === undefined || isNaN(amount)) return '-';
    
    const numAmount = parseFloat(amount);
    
    if (currency === 'USD') {
      return `$${numAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    } else {
      return `LRD ${numAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
  }, []);

  // Format amount with conversion shown
  // Returns: "LRD 5,000 (≈ $27.32)" or "$27.32 (≈ LRD 5,000)"
  const formatWithConversion = useCallback((amount, originalCurrency = 'LRD') => {
    if (amount === null || amount === undefined || isNaN(amount)) return '-';
    
    const numAmount = parseFloat(amount);
    
    if (displayCurrency === 'original') {
      // Show original with conversion in parentheses
      if (originalCurrency === 'LRD') {
        const usdAmount = numAmount * exchangeRates.lrd_to_usd;
        return `LRD ${numAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (≈ $${usdAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})`;
      } else {
        const lrdAmount = numAmount * exchangeRates.usd_to_lrd;
        return `$${numAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (≈ LRD ${lrdAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})`;
      }
    } else if (displayCurrency === 'USD') {
      // Convert everything to USD
      let usdAmount = numAmount;
      if (originalCurrency === 'LRD') {
        usdAmount = numAmount * exchangeRates.lrd_to_usd;
      }
      return `$${usdAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    } else if (displayCurrency === 'LRD') {
      // Convert everything to LRD
      let lrdAmount = numAmount;
      if (originalCurrency === 'USD') {
        lrdAmount = numAmount * exchangeRates.usd_to_lrd;
      }
      return `LRD ${lrdAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    
    return formatCurrency(numAmount, originalCurrency);
  }, [displayCurrency, exchangeRates, formatCurrency]);

  // Simple format for display (original currency only - no conversion text)
  const formatSimple = useCallback((amount, originalCurrency = 'LRD') => {
    if (amount === null || amount === undefined || isNaN(amount)) return '-';
    
    const numAmount = parseFloat(amount);
    
    if (displayCurrency === 'original' || displayCurrency === originalCurrency) {
      return formatCurrency(numAmount, originalCurrency);
    } else if (displayCurrency === 'USD') {
      let usdAmount = originalCurrency === 'LRD' ? numAmount * exchangeRates.lrd_to_usd : numAmount;
      return formatCurrency(usdAmount, 'USD');
    } else if (displayCurrency === 'LRD') {
      let lrdAmount = originalCurrency === 'USD' ? numAmount * exchangeRates.usd_to_lrd : numAmount;
      return formatCurrency(lrdAmount, 'LRD');
    }
    
    return formatCurrency(numAmount, originalCurrency);
  }, [displayCurrency, exchangeRates, formatCurrency]);

  const value = {
    displayCurrency,
    setDisplayCurrency,
    exchangeRates,
    loading,
    refreshRates: fetchRates,
    convertAmount,
    formatCurrency,
    formatWithConversion,
    formatSimple
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export default CurrencyContext;
